import { EventData, MatchOdds } from "../../models/EventData";
import { EventmarketsMap, PremiumSportsState } from "../../models/sportsBookState";
import {
  FETCH_EVENTS_BY_SPORT_TYPE_SUCCESS,
  INPLAY_EVENTS_BY_PROVIDER_SUCCESS,
  UPDATE_EVENT_ODDS_FROM_WS,
  SET_USER_BALANCE_SUCCESS,
  SET_USER_CURRENCY_SUCCESS,
  FETCH_EVENT_INFO_SUCCESS,
  UPDATE_EVENT_MARKETS_INFO,
  UPDATE_DREAM_EVENT_MARKETS_INFO,
  UPDATE_SECOUNDARY_MARKETS_INFO,
  UPDATE_FANCY_MARKETS_INFO,
  FETCH_PREMIUM_EVENT_INFO_SUCCESS,
  UPDATE_PREMIUM_ODDS_FROM_WS,
  SET_USER_EXPOSURE_SUCCESS,
  UPDATE_TOPIC_URLS,
  SET_IS_CONNECTED,
} from "./exchangeSportsActionTypes";

type Action = {
  type: string;
  payload: any;
};

const eventMarketGroups = [
  ["matchOdds", "MO"],
  ["bookmakers", "BM"],
  ["bookMaker", "BM"],
  ["fancyMarkets", "FANCY"],
  ["fancy", "FANCY"],
  ["otherMarkets", "MO"],
  ["secondaryMarkets", "MO"],
] as const;

const flattenEventMarkets = (eventInfo: EventData): EventmarketsMap => {
  const marketsMap: EventmarketsMap = {};

  for (const [groupName, fallbackMarketType] of eventMarketGroups) {
    const markets = eventInfo?.markets?.[groupName] || eventInfo?.[groupName];
    if (!Array.isArray(markets)) continue;

    for (const market of markets) {
      if (!market?.marketId) continue;
      marketsMap[market.marketId] = {
        ...market,
        marketType: market.marketType || fallbackMarketType,
        runners: Array.isArray(market.runners) ? [...market.runners] : [],
      };
    }
  }

  return marketsMap;
};

const initialState: PremiumSportsState = {
  balance: null,
  inplayEvents: null,
  currency: null,
  events: null,
  eventInfo: null,
  eventMarkets: {},
  premiumEventInfo: null,
  exposure: null,
  topicUrls: {
    prBaseUrl: "",
    prTopic: ""
  },
  isSocketConnected: false
};

const exchangeSportsReducer = (
  state = initialState,
  action: Action
): PremiumSportsState => {
  switch (action.type) {
    case SET_USER_BALANCE_SUCCESS: {
      return {
        ...state,
        balance: action.payload,
      };
    }

    case SET_USER_CURRENCY_SUCCESS: {
      return {
        ...state,
        currency: action.payload,
      };
    }

    case INPLAY_EVENTS_BY_PROVIDER_SUCCESS: {
      const events: EventData[] = action.payload;
      let inplayEventsMap = {};

      if (events !== null && events.length > 0) {
        for (const ev of events) {
          const sportName = ev.sportName;
          let sportEvents: EventData[] = inplayEventsMap[sportName]
            ? inplayEventsMap[sportName]
            : [];
          sportEvents.push(ev);
          inplayEventsMap[sportName] = sportEvents;
        }
      }

      inplayEventsMap = Object.keys(inplayEventsMap)
        .sort()
        .reduce((obj, key) => {
          obj[key] = inplayEventsMap[key];
          return obj;
        }, {});

      return {
        ...state,
        inplayEvents: inplayEventsMap,
      };
    }

    case FETCH_EVENTS_BY_SPORT_TYPE_SUCCESS: {
      return {
        ...state,
        events: action.payload,
      };
    }

    case FETCH_EVENT_INFO_SUCCESS: {
      const eInfo: EventData = action.payload;
      const eventMarkets = state.eventMarkets;
      const marketsMap = flattenEventMarkets(eInfo);

      for (const normalizedMarket of Object.values(marketsMap)) {
          if (!normalizedMarket.runners.length &&
            normalizedMarket.marketType === "FANCY" &&
            (normalizedMarket.yesRate || normalizedMarket.noRate)) {
            normalizedMarket.runners = [
              {
                runnerId: `${normalizedMarket.marketId}-yes`,
                runnerName: normalizedMarket.yesName || "YES",
                status: normalizedMarket.status,
                backPrices: normalizedMarket.yesRate
                  ? [{ price: normalizedMarket.yesRate, size: normalizedMarket.yesSize || 0 }]
                  : [],
              },
              {
                runnerId: `${normalizedMarket.marketId}-no`,
                runnerName: normalizedMarket.noName || "NO",
                status: normalizedMarket.status,
                backPrices: normalizedMarket.noRate
                  ? [{ price: normalizedMarket.noRate, size: normalizedMarket.noSize || 0 }]
                  : [],
              },
            ];
          }
      }

      return {
        ...state,
        eventInfo: eInfo,
        eventMarkets:
          Object.keys(marketsMap).length > 0 ? marketsMap : eventMarkets,
      };
    }

    case UPDATE_EVENT_ODDS_FROM_WS: {
      const eInfo = action.payload;
    
      const markets = eInfo.markets ? eInfo.markets.matchOdds : eInfo.matchOdds;
    
      let eventsData = state?.events ? [...state.events]: []; // Copying the events array
      const index = eventsData.findIndex(event => event.eventId === eInfo.eventId);
    
      if (index !== -1) {
        eventsData[index] = { ...eInfo }; // Copying eInfo to avoid mutation
      }
    
      let marketsMap: EventmarketsMap = {};
    
      if (eInfo.status && eInfo.status.toLowerCase() === "suspended") {
        marketsMap = { ...state.eventMarkets }; // Reverting back to original eventMarkets
      }
    
      if (markets.length > 0 && ["in_play", "upcoming"].includes(eInfo.status?.toLowerCase())) {
        markets.forEach(mr => {
          const updatedMarket = {
            ...mr,
            runners: [...mr.runners]
          };
    
          if (mr.marketName.toLowerCase() === "Total") {
            updatedMarket.runners.sort((a, b) => a.runnerName.localeCompare(b.runnerName));
          } else {
            updatedMarket.runners.sort((a, b) => b.runnerId - a.runnerId);
          }
    
          marketsMap[mr.marketId] = updatedMarket;
        });
      } else {
        for (const key of Object.keys(marketsMap)) {
          if (["active", "open"].includes(marketsMap[key].status.toLowerCase())) {
            marketsMap[key] = { ...marketsMap[key], status: "Suspended" }; // Copying market object and updating status
          }
        }
      }
    
      return {
        ...state,
        events: eventsData,
        eventInfo: eInfo,
        eventMarkets: marketsMap
      };
    };

    case UPDATE_DREAM_EVENT_MARKETS_INFO: {
      let dreamEventInfo = state.eventInfo;
      let moMarkets = action.payload;

      if (moMarkets && moMarkets.runners.length > 0) {
        moMarkets.runners.sort((a, b) => {
          const aDesc = a.runnerName;
          const bDesc = b.runnerName;
          if (aDesc > bDesc) return 1;
          else if (aDesc < bDesc) return -1;
          return 0;
        });
      }

      if (dreamEventInfo && !!dreamEventInfo.markets.matchOdds) {
        if (
          Array.isArray(dreamEventInfo?.markets?.matchOdds) &&
          dreamEventInfo?.markets?.matchOdds.length > 1
        ) {
          var foundIndex = dreamEventInfo.markets.matchOdds.findIndex(
            (x) => x.marketId == moMarkets.marketId
          );
          let data = {
            ...moMarkets,
            limits: dreamEventInfo?.markets?.matchOdds[foundIndex].limits,
          };
          dreamEventInfo.markets.matchOdds[foundIndex] = Object(data);
        } else {
          let data = {
            ...action?.payload,
            limits: dreamEventInfo?.markets?.matchOdds[0].limits,
          };
          dreamEventInfo.markets.matchOdds = [data];
        }
      }

      if (
        dreamEventInfo?.markets?.matchOdds &&
        dreamEventInfo?.markets?.matchOdds.length > 0
      ) {
        dreamEventInfo?.markets.matchOdds.sort((a, b) => {
          if (a.marketName > b.marketName) return 1;
          else if (a.marketName < b.marketName) return -1;
          return 0;
        });
      }

      return {
        ...state,
        // eventMarkets: marketsMap,
        eventInfo: dreamEventInfo,
      };
    }
    case FETCH_PREMIUM_EVENT_INFO_SUCCESS: {
      let pEventInfo = state.premiumEventInfo;
      const eventMarkets = state.eventMarkets;

      if (action.payload?.markets?.matchOdds?.length > 0) {
        pEventInfo = action.payload;
      }

      const markets: MatchOdds[] = action.payload?.markets?.matchOdds;
      let marketsMap = {};

      if (markets?.length > 0) {
        for (const mr of markets) {
          marketsMap[mr.marketId] = mr;
        }
      }

      return {
        ...state,
        eventMarkets: Object.keys(marketsMap).length > 0 ? marketsMap : eventMarkets,
        premiumEventInfo: pEventInfo ? pEventInfo : {},
      };
    }
    case UPDATE_EVENT_MARKETS_INFO: {
      const eInfo: EventData = action.payload;
      const markets: MatchOdds[] = eInfo.markets.matchOdds;
      let marketsMap = state.eventMarkets;

      for (const mr of markets) {
        if (mr.marketName.toLowerCase() === "Total") {
          mr.runners.sort((a, b) => {
            if (a.runnerName > b.runnerName) return 1;
            else return -1;
          });
        } else {
          mr.runners.sort((a, b) => {
            if (a.runnerId > b.runnerId) return -1;
            else return 1;
          });
        }
        marketsMap[mr.marketId] = mr;
      }
      eInfo.markets.matchOdds = [];

      return {
        ...state,
        eventInfo: eInfo,
        eventMarkets: marketsMap,
      };
    }
    case UPDATE_PREMIUM_ODDS_FROM_WS: {
      const eInfo: any = action.payload.msgObj;
      const type: string = action.payload.type;
      const markets: MatchOdds[] = eInfo.markets
        ? [...eInfo.markets.matchOdds]
        : [...eInfo.matchOdds];
      let marketsMap = {...state.premiumEventInfo};
      let matchOdds = [];

      if (marketsMap?.status === "SUSPENDED") {
        matchOdds = marketsMap?.markets
          ? [...marketsMap?.markets?.matchOdds]
          :[...marketsMap?.matchOdds];
      }
      if (marketsMap?.status) {
        marketsMap.status = eInfo.status;
      }
      if (type === "odds_update" && markets?.length > 0 && ["in_play", "upcoming"].includes(eInfo.status.toLowerCase())) {
        for (const mr of markets) {
          if (mr.marketName.toLowerCase() === "Total") {
            mr.runners.sort((a, b) => {
              if (a.runnerName > b.runnerName) return 1;
              else return -1;
            });
          } else {
            mr.runners.sort((a, b) => {
              if (a.runnerId > b.runnerId) return -1;
              else return 1;
            });
          }

          let ind = matchOdds?.findIndex(
            (item: any) => item.marketId === mr.marketId
          );
          if (["active", "open"].includes(mr?.status?.toLowerCase())) {
            ind > -1 ? (matchOdds[ind] = mr) : matchOdds?.push(mr);
          } else if (mr.status.toLowerCase() === "suspended") {
            ind > -1 ? (matchOdds[ind] = mr) : matchOdds?.push(mr);
          } else if (mr.status.toLowerCase() === "suspended") {
            ind > -1 && matchOdds.splice(ind, 1);
          }
          // marketsMap[mr.marketId] = mr;
        }
      } else {
        for (const mr of markets) {
          if (
            ["active", "open"].includes(mr.status.toLowerCase())
          ) {
            mr.status = "Suspended";
          }
        }
        matchOdds = markets;
      }
      if (marketsMap?.markets?.premiumMarkets) marketsMap.markets.premiumMarkets = matchOdds;
      return {
        ...state,
        premiumEventInfo: {
          ...marketsMap,
          eventStatus: eInfo.status,
          markets: { ...marketsMap?.markets, matchOdds },
        },
      };
    }

    case SET_USER_EXPOSURE_SUCCESS: {
      return {
        ...state,
        exposure: action.payload,
      };
    }

    case UPDATE_SECOUNDARY_MARKETS_INFO: {
      let bmMarket = action.payload;
      if (bmMarket && bmMarket.runners.length > 0) {
        bmMarket.runners.sort((a, b) => {
          const aDesc = a.runnerName;
          const bDesc = b.runnerName;
          if (aDesc > bDesc) return 1;
          else if (aDesc < bDesc) return -1;
          return 0;
        });
      }

      let dreamEventInfo = state.eventInfo;
      if (dreamEventInfo && !!dreamEventInfo.markets.bookmakers) {
        if (dreamEventInfo.markets.bookmakers.length === 1) {
          // dreamEventInfo.markets.bookmakers = [];
          let data = {
            ...action?.payload,
            limits: dreamEventInfo.markets.bookmakers[0]?.limits,
          };
          dreamEventInfo.markets.bookmakers = [data];
        } else {
          const bookmakerIndex = dreamEventInfo.markets.bookmakers.findIndex(
              (x) => x.marketId === bmMarket.marketId
          );

          const data = {
              ...bmMarket,
              limits: dreamEventInfo.markets.bookmakers[bookmakerIndex].limits,
          };

          dreamEventInfo.markets.bookmakers[bookmakerIndex] = data;
        }
      }

      if (
        dreamEventInfo.markets.bookmakers &&
        dreamEventInfo.markets.bookmakers.length > 0
      ) {
        dreamEventInfo.markets.bookmakers.sort((a, b) => {
          const aDesc = a.marketName;
          const bDesc = b.marketName;
          if (aDesc > bDesc) return 1;
          else if (aDesc < bDesc) return -1;
          return 0;
        });
      }
      return {
        ...state,
        eventInfo: dreamEventInfo,
      };
    }

    case UPDATE_FANCY_MARKETS_INFO: {
      let fmMarket = action.payload;
      let dreamEventInfo = state.eventInfo;
      let data = [];

      for (let i = 0; i < fmMarket.length; i++) {
        data.push({
          ...fmMarket[i],
          limits: dreamEventInfo?.markets?.fancyMarkets
            ? dreamEventInfo?.markets?.fancyMarkets[0]?.limits
            : null,
        });
      }

      if (fmMarket) dreamEventInfo.markets.fancyMarkets = data;

      if (
        dreamEventInfo.markets.fancyMarkets &&
        dreamEventInfo.markets.fancyMarkets.length > 0
      ) {
        dreamEventInfo.markets.fancyMarkets.sort((a, b) => {
          const aDesc = a.marketName;
          const bDesc = b.marketName;
          if (aDesc > bDesc) return 1;
          else if (aDesc < bDesc) return -1;
          return 0;
        });
      }

      return {
        ...state,
        eventInfo: dreamEventInfo,
      };
    }

    case UPDATE_TOPIC_URLS: {
      return {
        ...state,
        topicUrls: action.payload,
      };
    }
    
    case SET_IS_CONNECTED: {
      return {
        ...state,
        isSocketConnected: action.payload,
      };
    }

    default:
      return state;
  }
};

export default exchangeSportsReducer;
